"use client";
import * as React from "react";
import { useSearchParams, usePathname } from "next/navigation";
import { useEffect, useMemo } from "react";
import { InstantSearch, Configure } from "react-instantsearch";
import {
  useCurrentRefinements,
  useRefinementList,
  useClearRefinements,
} from "react-instantsearch";
import type { CurrentRefinementsConnectorParamsRefinement } from "instantsearch.js/es/connectors/current-refinements/connectCurrentRefinements";
import { cn } from "@/lib/utils";
import {
  DEFAULT_SEARCH_CONFIG,
  QUERY_URL_PARAM,
  RESULT_PRODUCT_TYPE_MAP,
} from "@/components/Search/constants";
import { PRODUCT_INDEX_EN } from "@/constants/algolia";
import { SearchContextProvider } from "@/components/Search/components/SearchContextProvider";
import getFilters from "@/components/Search/utils/getFilters";
import getResultTypeFromUrl from "@/components/Search/utils/getResultTypeFromUrl";
import { ExploreContent } from "./ExploreContent";
import getPublicClient from "@/lib/algolia/getPublicClient";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import { ElementType } from "@/constants/tagular/main";
import { getFacetAttributeName } from "@/components/Search/utils/getFacetAttributeName";
import useSearchInitialState from "@/components/Search/hooks/useSearchInitialState";
import FilterDropdown from "@/components/Search/components/SearchFilters/components/Dropdown";
import useTranslations from "@/i18n/useTranslations";

const searchClient = getPublicClient();

interface ExploreDegreeHubRedesignProps {
  title: string;
  titleStyles?: string;
  containerStyles?: string;
}

function DegreeHubContent({ indexName }: { indexName: string }) {
  const t = useTranslations("Common");
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const learningTypeFacet = getFacetAttributeName("learning_type", {
    indexName,
  });
  const { items: learningTypeItems, refine: refineLearningType } =
    useRefinementList({
      attribute: learningTypeFacet,
    });
  const { items: currentRefinements, refine: refineRefinement } =
    useCurrentRefinements();
  const { refine: clearAllRefinements } = useClearRefinements({
    excludedAttributes: [learningTypeFacet],
  });

  // Element events for Clear All button
  const { elementClicked: clearAllClicked } = useElementEvents({
    webElement: {
      elementType: ElementType.Button,
      htmlId: "filter-clear-all",
      location: "degree-hub-filters",
      position: "2",
      text: "Clear All",
    },
  });
  const getLearningTypeFromPath = (path: string): string | null => {
    const lowerPath = path.toLowerCase();
    if (lowerPath.includes("/bachelors")) {
      return "Bachelors";
    } else if (lowerPath.includes("/masters")) {
      return "Masters";
    } else if (lowerPath.includes("/doctorate")) {
      return "Doctorate";
    }
    return null;
  };
  const searchQuery = searchParams.get(QUERY_URL_PARAM);
  const pageTab = RESULT_PRODUCT_TYPE_MAP[getResultTypeFromUrl(searchParams)];
  const pathLearningType = getLearningTypeFromPath(pathname);
  useEffect(() => {
    if (pathLearningType) {
      const isPathLearningTypeActive = learningTypeItems.some(
        (item) => item.value === pathLearningType && item.isRefined,
      );
      if (!isPathLearningTypeActive) {
        learningTypeItems.forEach((item) => {
          if (item.isRefined) {
            refineLearningType(item.value);
          }
        });
        refineLearningType(pathLearningType);
      }
    }
  }, [pathLearningType, learningTypeItems, refineLearningType]);

  const configForSearch = useMemo(() => {
    const config = {
      ...DEFAULT_SEARCH_CONFIG,
      filters: getFilters({ indexName }),
      hitsPerPage: 100,
    };
    if (pageTab) {
      config.facetsRefinements = { product: [pageTab] };
    }
    if (searchQuery) {
      config.query = searchQuery;
    }
    return config;
  }, [pageTab, searchQuery, indexName]);
  const handleClearAll = () => {
    if (typeof window === "undefined") return;

    const subcategory = pathname?.replace("/", "") || "";

    const eventData = {
      webContext: {
        page: {
          url: pathname || "",
        },
      },
      customDimensions: [
        { key: "group", value: "degree-hub" },
        { key: "subcategory", value: subcategory },
      ],
    };

    clearAllClicked(eventData);

    clearAllRefinements();
    if (pathLearningType) {
      setTimeout(() => {
        refineLearningType(pathLearningType);
      }, 0);
    }
  };

  // Element events for pill remove buttons
  const { elementClicked: pillRemoveClicked } = useElementEvents({
    webElement: {
      elementType: ElementType.Button,
      htmlId: "filter-pill-remove",
      location: "degree-hub-filters",
      position: "3",
      text: "Remove filter",
    },
  });

  // Handle pill remove button click
  const handlePillRemove = (
    refinement: CurrentRefinementsConnectorParamsRefinement,
  ) => {
    if (typeof window === "undefined") return;

    // Extract page subcategory from pathname
    const subcategory = pathname?.replace("/", "") || "";

    const eventData = {
      webContext: {
        page: {
          url: pathname || "",
        },
      },
      customDimensions: [
        { key: "group", value: "degree-hub" },
        { key: "subcategory", value: subcategory },
      ],
    };

    pillRemoveClicked(eventData);
    refineRefinement(refinement);
  };
  const filterableRefinements = currentRefinements.filter(
    (item) => item.attribute !== learningTypeFacet,
  );
  const totalFilterableRefinements = filterableRefinements.reduce(
    (total, item) => total + item.refinements.length,
    0,
  );
  return (
    <>
      <Configure {...configForSearch} />
      {totalFilterableRefinements > 0 && (
        <div className="mt-6">
          <div className="flex items-center flex-wrap gap-2">
            {filterableRefinements.map((item) =>
              item.refinements.map((refinement) => (
                <div
                  key={`${item.attribute}-${refinement.value}`}
                  className="inline-flex items-center bg-[#F3F1ED] border border-[#E7E4DB] rounded-[6px] py-1.5 px-3 text-xs gap-2 transition-all"
                >
                  <span className="text-secondary-500">{refinement.label}</span>
                  <button
                    onClick={() => handlePillRemove(refinement)}
                    className="!no-underline bg-transparent border-0 cursor-pointer p-0 text-black -mt-1 flex items-center justify-center text-base w-4 h-4 rounded-full transition-all hover:bg-red-500 hover:text-white"
                    title={t("removeFilter")}
                  >
                    ×
                  </button>
                </div>
              )),
            )}
            <button
              id="filter-clear-all"
              onClick={handleClearAll}
              className="text-sm text-gray-800 ml-2.5 font-medium cursor-pointer transition-all hover:bg-gray-50 hover:border-gray-400 px-2 py-1 rounded"
            >
              {t("clearAll")}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export const ExploreDegreeHubRedesign = ({
  title,
  titleStyles = "",
  containerStyles = "",
}: ExploreDegreeHubRedesignProps) => {
  const indexName = PRODUCT_INDEX_EN;
  const filterCategories = [
    getFacetAttributeName("subject", { indexName }),
    "partner",
  ];
  const pathname = usePathname();
  const getLearningTypeFromPath = (path: string): string | null => {
    const lowerPath = path.toLowerCase();
    if (lowerPath.includes("/bachelors")) {
      return "Bachelors";
    } else if (lowerPath.includes("/masters")) {
      return "Masters";
    } else if (lowerPath.includes("/doctorate")) {
      return "Doctorate";
    }
    return null;
  };
  const pathLearningType = getLearningTypeFromPath(pathname);
  const baseInitialSearchState = useSearchInitialState(indexName, "");
  const learningTypeFacet = getFacetAttributeName("learning_type", {
    indexName,
  });
  const initialSearchState = useMemo(() => {
    const state = { ...baseInitialSearchState };
    if (pathLearningType) {
      if (!state[indexName]) {
        state[indexName] = {};
      }
      if (!state[indexName].refinementList) {
        state[indexName].refinementList = {};
      }
      state[indexName].refinementList[learningTypeFacet] = [pathLearningType];
    }
    return state;
  }, [baseInitialSearchState, indexName, pathLearningType, learningTypeFacet]);
  return (
    <SearchContextProvider searchIndex={indexName}>
      <InstantSearch
        searchClient={searchClient}
        indexName={indexName}
        initialUiState={initialSearchState}
        future={{
          preserveSharedStateOnUnmount: true,
        }}
      >
        <div className={cn("bg-[#FBFAF9]", containerStyles)}>
          <h2 className={cn("mb-8 text-2xl md:text-3xl", titleStyles)}>
            {title}
          </h2>
          <div className="flex items-center overflow-x-auto gap-4 scrollbar-thin relative w-full">
            <span className="text-base whitespace-nowrap text-primary-500">
              Filter by:
            </span>
            {filterCategories.map((facet) => (
              <FilterDropdown
                key={facet}
                filterCategory={facet}
                contentClassName="z-20"
                labelFontSize="text-md"
              />
            ))}
          </div>
          <DegreeHubContent indexName={indexName} />
          <ExploreContent />
        </div>
      </InstantSearch>
    </SearchContextProvider>
  );
};
